// @ts-check
import { useChat } from "@ai-sdk/react"
import { useCallback, useMemo, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { DefaultChatTransport, generateId, isToolUIPart } from "ai"
import * as requester from "../../../../../services/base/api.service"
import {
  createChat,
  generateChatTitle,
  getChatById,
  chatMessagesUrl,
  uploadChatAttachment,
} from "../../../../../services/estimations/estimationAgentChat.service"
import useToast from "../../../../../hooks/useToast.hook"
import {
  ESTIMATION_AGENT_MAX_ATTACHMENTS,
  ESTIMATION_AGENT_MAX_FILE_BYTES,
  isAllowedEstimationAttachmentFile,
  mediaTypeForEstimationFile,
} from "./estimationAgentAttachments.constants"
import { LOCAL_NEW_CHAT_ID } from "./estimationAgentChat.constants"
import { normalizeStoredUIMessages } from "./storedUIMessages"
import {
  hasPendingAskQuestionsInput,
  normalizeAssistantPartsAfterUserAbort,
} from "./uiMessageParts"

/**
 * @param {any} resource
 * @returns {string}
 */
function fetchUrlToString(resource) {
  if (typeof resource === "string") return resource
  if (typeof URL !== "undefined" && resource instanceof URL)
    return resource.href
  if (typeof Request !== "undefined" && resource instanceof Request)
    return resource.url
  return String(resource)
}

/**
 * Evita que `safeValidateUIMessages` falle en el backend: placeholders del asistente con
 * `parts: []` y duplicados del mismo `id` por fusión/streaming.
 *
 * @param {import("ai").UIMessage[]} messages
 * @returns {import("ai").UIMessage[]}
 */
function sanitizeUIMessagesForBackendRequest(messages) {
  const list = Array.isArray(messages) ? messages : []
  const withParts = list.filter((m) => {
    const parts = m?.parts
    return Array.isArray(parts) && parts.length > 0
  })
  /** @type {Map<string, number>} */
  const lastIndexById = new Map()
  for (let i = 0; i < withParts.length; i++) {
    const id = withParts[i]?.id
    if (typeof id === "string" && id.length > 0) lastIndexById.set(id, i)
  }
  return withParts.filter((m, i) => {
    const id = m?.id
    if (typeof id !== "string" || id.length === 0) return true
    return lastIndexById.get(id) === i
  })
}

/**
 * Reanudación automática tras `addToolOutput` / `addToolApprovalResponse`: el último paso del
 * mensaje del asistente debe tener todas las herramientas no ejecutadas por el proveedor en
 * estado terminal. Los helpers del SDK por separado fallan si solo hay tools de cliente
 * (`ask_questions`) o solo aprobaciones — el AND devolvía false.
 *
 * @param {{ messages: import("ai").UIMessage[] }} opts
 */
function lastAssistantMessageLastStepClientToolsResolved({ messages }) {
  const message = messages[messages.length - 1]
  if (!message || message.role !== "assistant") return false
  const parts = message.parts ?? []
  const lastStepStartIndex = parts.reduce((lastIndex, part, index) => {
    return part.type === "step-start" ? index : lastIndex
  }, -1)
  const lastStepTools = parts
    .slice(lastStepStartIndex + 1)
    .filter(isToolUIPart)
    .filter((part) => !part.providerExecuted)
  if (lastStepTools.length === 0) return false
  return lastStepTools.every(
    (part) =>
      part.state === "output-available" ||
      part.state === "output-error" ||
      part.state === "approval-responded"
  )
}

/**
 * @returns {{
 *   messages: import("ai").UIMessage[];
 *   draft: string;
 *   setDraft: React.Dispatch<React.SetStateAction<string>>;
 *   chat: import("./estimationAgent.types").Chat | null;
 *   handleSend: () => Promise<void>;
 *   handleStop: () => void;
 *   isCreatingChat: boolean;
 *   isResponseStreaming: boolean;
 *   headerSubtitle: string | undefined;
 *   subtitleLoading: boolean;
 *   chatError: Error | undefined;
 *   clearChatError: () => void;
 *   activeChatId: string | null;
 *   selectChat: (chatId: string) => Promise<boolean>;
 *   isLoadingChatDetail: boolean;
 *   loadChatDetailError: string | null;
 *   clearLoadChatDetailError: () => void;
 *   addToolApprovalResponse: import("ai").ChatAddToolApproveResponseFunction;
 *   addToolOutput: import("ai").ChatAddToolOutputFunction<import("ai").UIMessage>;
 *   composerLocked: boolean;
 *   composerAttachments: Array<{
 *     id: string;
 *     phase: "uploading" | "ready" | "error";
 *     localName: string;
 *     mediaType: string;
 *     url?: string;
 *     error?: string;
 *   }>;
 *   removeComposerAttachment: (id: string) => void;
 *   addComposerFiles: (files: FileList | File[] | null) => Promise<void>;
 *   isComposerUploading: boolean;
 *   notifyPasteFilesystemUriOnly: () => void;
 *   syncChatTitle: (chatId: string, title: string) => void;
 * }}
 */
export function useEstimationAgent() {
  const clientChatId = useRef(generateId())
  const serverChatIdRef = useRef(/** @type {string | null} */ (null))
  const uploadAbortMap = useRef(new Map())
  const { setErrorMessage } = useToast()

  const [draft, setDraft] = useState("")
  const [chat, setChat] = useState(
    /** @type {import("./estimationAgent.types").Chat | null} */ (null)
  )
  const [isCreatingChat, setIsCreatingChat] = useState(false)
  const [isTitleGenerating, setIsTitleGenerating] = useState(false)
  const titleGenerationSeq = useRef(0)
  const [isLoadingChatDetail, setIsLoadingChatDetail] = useState(false)
  const [loadChatDetailError, setLoadChatDetailError] = useState(
    /** @type {string | null} */ (null)
  )

  const [composerAttachments, setComposerAttachments] = useState(
    /** @type {Array<{
     *   id: string;
     *   phase: "uploading" | "ready" | "error";
     *   localName: string;
     *   mediaType: string;
     *   url?: string;
     *   error?: string;
     * }>} */ ([])
  )

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: chatMessagesUrl("__invalid__"),
        fetch: async (url, init) => {
          if (init == null) {
            return /** @type {Promise<Response>} */ (
              globalThis.fetch(/** @type {any} */ (url))
            )
          }
          const raw = init.body
          if (init.method !== "POST" || typeof raw !== "string") {
            return /** @type {Promise<Response>} */ (
              globalThis.fetch(/** @type {any} */ (url), init)
            )
          }
          let payload
          try {
            payload = JSON.parse(raw)
          } catch {
            return /** @type {Promise<Response>} */ (
              globalThis.fetch(/** @type {any} */ (url), init)
            )
          }
          return requester.post(fetchUrlToString(url), payload, {
            signal: init.signal,
          })
        },
        prepareSendMessagesRequest: async (opts) => {
          const sid = serverChatIdRef.current
          if (!sid) {
            throw new Error("Chat no disponible")
          }
          const msgs = opts.messages
          const messageList = sanitizeUIMessagesForBackendRequest(
            Array.isArray(msgs) ? msgs : []
          )
          return {
            api: chatMessagesUrl(sid),
            body: {
              id: sid,
              messages: messageList,
              trigger: opts.trigger,
              messageId: opts.messageId,
            },
          }
        },
      }),
    []
  )

  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    error: chatError,
    clearError: clearChatError,
    addToolApprovalResponse,
    addToolOutput,
  } = useChat({
    id: clientChatId.current,
    messages: [],
    transport,
    sendAutomaticallyWhen: lastAssistantMessageLastStepClientToolsResolved,
    onError: (err) => {
      console.error("[estimation-agent]", err)
    },
  })

  const handleStop = useCallback(async () => {
    try {
      await stop()
    } catch {
      // abort
    }
    setMessages((prev) => {
      if (prev.length === 0) return prev
      const last = prev[prev.length - 1]
      if (last.role !== "assistant") return prev
      const parts = last.parts ?? []
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          parts: normalizeAssistantPartsAfterUserAbort(parts),
        },
      ]
    })
  }, [stop, setMessages])

  const clearLoadChatDetailError = useCallback(() => {
    setLoadChatDetailError(null)
  }, [])

  const composerLocked = useMemo(
    () => hasPendingAskQuestionsInput(messages),
    [messages]
  )

  const isComposerUploading = useMemo(
    () => composerAttachments.some((a) => a.phase === "uploading"),
    [composerAttachments]
  )

  const notifyPasteFilesystemUriOnly = useCallback(() => {
    setErrorMessage({
      title: "Adjuntos",
      message:
        "El portapapeles solo trae un enlace al archivo (no el archivo). Usa el botón + para elegirlo.",
    })
  }, [setErrorMessage])

  const removeComposerAttachment = useCallback((/** @type {string} */ id) => {
    const ac = uploadAbortMap.current.get(id)
    if (ac) {
      ac.abort()
      uploadAbortMap.current.delete(id)
    }
    setComposerAttachments((prev) => prev.filter((x) => x.id !== id))
  }, [])

  const addComposerFiles = useCallback(
    async (/** @type {FileList | File[] | null} */ incoming) => {
      if (!incoming || composerLocked) return
      const raw = Array.from(incoming)
      if (raw.length === 0) return

      /** @type {File[]} */
      const accepted = []
      for (const file of raw) {
        if (!(file instanceof File)) continue
        if (file.size > ESTIMATION_AGENT_MAX_FILE_BYTES) {
          setErrorMessage({
            title: "Adjuntos",
            message: `El archivo supera 15 MB: ${file.name}`,
          })
          continue
        }
        if (!isAllowedEstimationAttachmentFile(file)) {
          setErrorMessage({
            title: "Adjuntos",
            message: "Solo se permiten PDF o imágenes.",
          })
          continue
        }
        accepted.push(file)
      }
      if (accepted.length === 0) return

      /** @type {Array<{ id: string; file: File; localName: string; mediaType: string }>} */
      const uploadWork = []

      flushSync(() => {
        setComposerAttachments((prev) => {
          const room = ESTIMATION_AGENT_MAX_ATTACHMENTS - prev.length
          if (room <= 0) {
            queueMicrotask(() =>
              setErrorMessage({
                title: "Adjuntos",
                message: "Máximo 5 archivos por mensaje.",
              })
            )
            return prev
          }
          const slice = accepted.slice(0, room)
          if (accepted.length > room) {
            queueMicrotask(() =>
              setErrorMessage({
                title: "Adjuntos",
                message: "Solo se pueden añadir hasta 5 archivos por mensaje.",
              })
            )
          }
          const queue = slice.map((file) => ({
            id: generateId(),
            file,
            localName: file.name,
            mediaType: mediaTypeForEstimationFile(file),
          }))
          uploadWork.length = 0
          uploadWork.push(...queue)
          return [
            ...prev,
            ...queue.map((q) => ({
              id: q.id,
              phase: /** @type {const} */ ("uploading"),
              localName: q.localName,
              mediaType: q.mediaType,
            })),
          ]
        })
      })

      if (uploadWork.length === 0) {
        return
      }

      for (const q of uploadWork) {
        let chatId =
          serverChatIdRef.current ??
          (typeof chat?.id === "string" ? chat.id : null)
        if (!chatId) {
          setIsCreatingChat(true)
          try {
            const created = await createChat()
            if (!created.success) {
              setComposerAttachments((prev) =>
                prev.map((it) =>
                  it.id === q.id ?
                    {
                      ...it,
                      phase: "error",
                      error: "No se pudo crear el chat",
                    }
                  : it
                )
              )
              continue
            }
            chatId = created.chatId
            serverChatIdRef.current = chatId
            setChat({ id: chatId })
          } finally {
            setIsCreatingChat(false)
          }
        }

        const ac = new AbortController()
        uploadAbortMap.current.set(q.id, ac)
        try {
          const up = await uploadChatAttachment(chatId, q.file, {
            signal: ac.signal,
          })
          uploadAbortMap.current.delete(q.id)
          if (!up.success) {
            setComposerAttachments((prev) =>
              prev.map((it) =>
                it.id === q.id ?
                  {
                    ...it,
                    phase: "error",
                    error: up.error,
                  }
                : it
              )
            )
            continue
          }
          setComposerAttachments((prev) =>
            prev.map((it) =>
              it.id === q.id ?
                {
                  ...it,
                  phase: "ready",
                  url: up.url,
                }
              : it
            )
          )
        } catch (e) {
          uploadAbortMap.current.delete(q.id)
          const err = /** @type {Error & { name?: string }} */ (e)
          if (err?.name === "AbortError") continue
          setComposerAttachments((prev) =>
            prev.map((it) =>
              it.id === q.id ?
                {
                  ...it,
                  phase: "error",
                  error: err?.message || "Error al subir",
                }
              : it
            )
          )
        }
      }
    },
    [composerLocked, setErrorMessage, chat]
  )

  const handleSend = useCallback(async () => {
    const text = draft.trim()
    const readyFiles = composerAttachments.filter(
      (a) => a.phase === "ready" && typeof a.url === "string"
    )
    if (isComposerUploading) return
    if (!text && readyFiles.length === 0) return
    if (isCreatingChat) return
    if (status === "submitted" || status === "streaming") return
    if (hasPendingAskQuestionsInput(messages)) return

    clearLoadChatDetailError()

    /** @type {import("./estimationAgent.types").Chat | null} */
    let effectiveChat = chat

    if (!effectiveChat) {
      setIsCreatingChat(true)
      try {
        const result = await createChat()
        if (!result.success) {
          console.error("No se pudo crear el chat")
          return
        }
        effectiveChat = { id: result.chatId }
        serverChatIdRef.current = result.chatId
        setChat(effectiveChat)
      } finally {
        setIsCreatingChat(false)
      }
    } else if (!serverChatIdRef.current) {
      serverChatIdRef.current = effectiveChat.id
    }

    if (!effectiveChat || !serverChatIdRef.current) return

    /** @type {import("ai").FileUIPart[]} */
    const fileParts = readyFiles.map((a) => ({
      type: /** @type {const} */ ("file"),
      url: /** @type {string} */ (a.url),
      mediaType: a.mediaType,
      filename: a.localName,
    }))

    const titleSource =
      text || readyFiles[0]?.localName || "Mensaje con adjuntos"

    const needsTitle = !effectiveChat.title?.trim()
    if (needsTitle) {
      const chatIdForTitle = effectiveChat.id
      const seq = ++titleGenerationSeq.current
      setIsTitleGenerating(true)
      generateChatTitle(chatIdForTitle, titleSource)
        .then((result) => {
          if (result.success) {
            setChat((c) =>
              c?.id === chatIdForTitle ? { ...c, title: result.title } : c
            )
          }
        })
        .finally(() => {
          if (titleGenerationSeq.current === seq) {
            setIsTitleGenerating(false)
          }
        })
    }

    setDraft("")
    setComposerAttachments([])
    clearChatError()
    try {
      if (fileParts.length > 0 && text) {
        await sendMessage({ text, files: fileParts })
      } else if (fileParts.length > 0) {
        await sendMessage({ files: fileParts })
      } else {
        await sendMessage({ text })
      }
    } catch (e) {
      const err = /** @type {Error} */ (e)
      console.error("[estimation-agent] sendMessage", err)
    }
  }, [
    draft,
    chat,
    isCreatingChat,
    status,
    messages,
    sendMessage,
    clearChatError,
    clearLoadChatDetailError,
    composerAttachments,
    isComposerUploading,
  ])

  const selectChat = useCallback(
    /** @param {string} chatId */
    async (chatId) => {
      if (chatId === LOCAL_NEW_CHAT_ID) {
        try {
          await stop()
        } catch {
          // abort
        }
        clearChatError()
        setLoadChatDetailError(null)
        setMessages([])
        setChat(null)
        serverChatIdRef.current = null
        titleGenerationSeq.current += 1
        setIsTitleGenerating(false)
        setDraft("")
        uploadAbortMap.current.forEach((ac) => ac.abort())
        uploadAbortMap.current.clear()
        setComposerAttachments([])
        return true
      }

      setIsLoadingChatDetail(true)
      setLoadChatDetailError(null)
      try {
        try {
          await stop()
        } catch {
          // abort
        }
        clearChatError()
        uploadAbortMap.current.forEach((ac) => ac.abort())
        uploadAbortMap.current.clear()
        setComposerAttachments([])
        const result = await getChatById(chatId)
        if (!result.success) {
          setLoadChatDetailError("No se pudo cargar el chat")
          return false
        }
        const { id, title, messages: rawMsgs } = result.data
        const uiMessages = normalizeStoredUIMessages(rawMsgs)
        serverChatIdRef.current = id
        setChat({
          id,
          title: title?.trim() ? title : undefined,
        })
        setMessages(uiMessages)
        titleGenerationSeq.current += 1
        setIsTitleGenerating(false)
        return true
      } catch (e) {
        console.error("[estimation-agent] selectChat", e)
        setLoadChatDetailError("No se pudo cargar el chat")
        return false
      } finally {
        setIsLoadingChatDetail(false)
      }
    },
    [stop, clearChatError, setMessages]
  )

  const titleTrim = chat?.title?.trim()
  const headerSubtitle =
    titleTrim || (messages.length === 0 ? "Nuevo chat" : undefined)

  const isResponseStreaming = status === "submitted" || status === "streaming"

  const activeChatId = chat?.id ?? null

  const syncChatTitle = useCallback(
    /** @param {string} chatId @param {string} title */
    (chatId, title) => {
      setChat((c) => (c && c.id === chatId ? { ...c, title } : c))
    },
    []
  )

  return {
    messages,
    draft,
    setDraft,
    chat,
    handleSend,
    handleStop,
    isCreatingChat,
    isResponseStreaming,
    headerSubtitle,
    subtitleLoading: isTitleGenerating,
    chatError,
    clearChatError,
    activeChatId,
    selectChat,
    isLoadingChatDetail,
    loadChatDetailError,
    clearLoadChatDetailError,
    addToolApprovalResponse,
    addToolOutput,
    composerLocked,
    composerAttachments,
    removeComposerAttachment,
    addComposerFiles,
    isComposerUploading,
    notifyPasteFilesystemUriOnly,
    syncChatTitle,
  }
}
